import { auth } from '../../../firebase/config'
import { getFCMToken, getNotificationPermission } from '../../../helpers/notificationHelpers'
import { addCouponForReferrer, processOrder, processProductOrder } from './functions'
import { CheckoutItem } from './types'
import { getLogoutPrompt } from './prompts'
import { setCheckoutState } from '../../../state/Slices/checkoutSlice'
import { Platform } from 'react-native'
import { showAlert } from '../../../state/Slices/settingsSlice'
import Colors from '../../../constants/Colors'

export interface PlaceOrderData {
	userId: string
	loadedData: any
	items: CheckoutItem[]
	orderNote: string
	couponCode: string
	useNewPaymentMethod: boolean
	couponCodeBackup: string
	restaurantDiscountAmount: number
	isDineIn: boolean
	tableNumber: string
	takeOutName: string
	prices: any
	dispatch: any
	cardId: string
	validCouponCodes: string[]
	loading: boolean
	tipCount: number
	selectedMethods: any
	posExtraData: { name: string; firebaseId: string; note: string; type: string }
	timestampData: any
	toggleConfirmModal: () => void
	redeemedRewards: any
	pickupPointObject: any
	pointsEarned: any
	isGuestCheckout: boolean
	navigation: any
	cartPrep: any
	restaurants: any
	isTestMode: boolean
	isDelivery: boolean
	restaurantIds: string[]
	deliveryOrgReceivedPercent: number
	localTimezone: string
	giftCard: any
	products?: any[]
	userLocation: any
	typePOS?: 'cash' | 'pinpad' | 'manual' | ''
}

type OrderData = {
	data: {
		created: boolean
		orderNumber: number
		orderId: string
		productId: string
		productDetails: {
			amount: number
		}
	}
	orderNumDisplay: number
	orderId: string
	orderNumber: number
	created: boolean
	isProductOrder: boolean
	productType: string
}

async function getFCM() {
	if (!global.kiosk && Platform.OS !== 'web') {
		const isNotificationPermitted = await getNotificationPermission()
		return isNotificationPermitted ? await getFCMToken() : ''
	}
}

/**
 *
 * @param data
 * @param paymentProvider
 * Places order by returning async function
 */
export function placeOrder(data: PlaceOrderData, paymentProvider) {
	return async () => {
		if (!auth.currentUser && !data.isGuestCheckout) {
			// not signed in
			data.toggleConfirmModal()
			data.dispatch(
				showAlert({
					title: 'Not signed in',
					body: 'You must sign in before placing your order',
					buttonA: 'Cancel',
					buttonB: 'Sign in',
					colorA: Colors.greyscale[5],
					boldButton: 'B',
					onPressB: () => {
						data.navigation.navigate('AccountStack', {
							screen: 'Account',
							params: { signup: false },
						})
					},
				})
			)

			return null
		}

		data.dispatch(setCheckoutState({ spinner: true, isOrderProcessing: true }))
		let couponVals = []
		if (data.validCouponCodes !== undefined) {
			couponVals = Object.values(data.validCouponCodes)
		}

		const i = couponVals
			.map(function (e) {
				if (e.couponCode) return e.couponCode.trim().toLowerCase()
				return null
			})
			.indexOf(data.couponCode.trim().toLowerCase())

		if ((data.cardId === '' || data.loading) && !data.useNewPaymentMethod && !global.kiosk) {
			data.toggleConfirmModal()
			data.dispatch(setCheckoutState({ spinner: false, isOrderProcessing: false }))
			return null
		}

		let processedOrder
		const FCMToken = await getFCM()

		try {
			if (auth.currentUser || data.isGuestCheckout) {
				//get restaurant details for order
				//get tax details
				processedOrder = await processOrder({
					paymentProvider,
					prices: data.prices,
					items: data.items,
					orderNote: data.orderNote,
					discount: couponVals[i],
					tip: data.tipCount,
					userId: data.userId,
					isGuestCheckout: data.isGuestCheckout,
					restaurants: data.restaurants,
					tableNumber: data.tableNumber,
					isDineIn: data.isDineIn,
					pickupName: data.takeOutName,
					restaurantDiscountAmount: data.restaurantDiscountAmount,
					selectedMethods: data.selectedMethods,
					posExtraData: data.posExtraData,
					redeemedRewards: data.redeemedRewards,
					pointsEarned: data.pointsEarned,
					timestampData: data.timestampData,
					pickupPointObject: data.pickupPointObject,
					isSandbox: data.loadedData[global.org].environment === 'sandbox' || data.isTestMode,
					cartPrep: data.cartPrep,
					FCMToken,
					loadedData: data.loadedData,
					isDelivery: data.isDelivery,
					restaurantIds: data.restaurantIds,
					deliveryOrgReceivedPercent: data.deliveryOrgReceivedPercent,
					localTimezone: data.localTimezone,
					userLocation: data.userLocation,
					typePOS: data.typePOS,
				})
			} else {
				return getLogoutPrompt(data.navigation)
			}
		} catch (error) {
			console.log(error)
			data.toggleConfirmModal()
			data.dispatch(setCheckoutState({ spinner: false }))
			data.dispatch(
				showAlert({
					title: 'Error placing order',
					body: 'Please try again.',
				})
			)
		} finally {
			data.dispatch(setCheckoutState({ isOrderProcessing: false }))
		}

		if (processedOrder !== 'PaymentError' && processedOrder.data) {
			console.log(
				`[Checkout.Helpers.placeOrder] Processed order created state: ${processedOrder.created}.`
			)
			data.dispatch(
				setCheckoutState({
					orderId: processedOrder.orderId,
					orderNumber: processedOrder.orderNumber,
					orderCreated: processedOrder.created,
				})
			)

			if (data.couponCodeBackup !== '') {
				await addCouponForReferrer(data.couponCodeBackup, data.userId)
			}
		}

		return processedOrder
	}
}

export function placeProductOrder(data: PlaceOrderData, paymentProvider) {
	data.dispatch(setCheckoutState({ spinner: false, loading: false }))

	return async () => {
		data.dispatch(setCheckoutState({ spinner: true }))
		if ((data.cardId === '' || data.loading) && !data.useNewPaymentMethod && !global.kiosk) {
			console.log(
				'[Checkout.Helpers.placeProductOrder] Toggle confirm modal and dispatch spinner false.'
			)
			data.toggleConfirmModal()
			data.dispatch(setCheckoutState({ spinner: false }))
			return null
		}

		console.log('[Checkout.Helpers.placeProductOrder] Attempting to process order.')
		if (!auth.currentUser && !data.isGuestCheckout) {
			console.log('[Checkout.Helpers.placeProductOrder] Returning logout prompt.')
			return getLogoutPrompt(data.navigation)
		}

		console.log('[Checkout.Helpers.placeProductOrder] Processing product order.')
		let processedOrder: OrderData

		console.log('[Checkout.Helpers.placeProductOrder] Getting FCM token.')
		const FCMToken = await getFCM() // Push notifications

		try {
			processedOrder = await processProductOrder({
				paymentProvider,
				prices: data.prices,
				items: data.items,
				orderNote: data.orderNote,
				discount: null,
				tip: data.tipCount,
				userId: data.userId,
				isGuestCheckout: data.isGuestCheckout,
				restaurants: data.restaurants,
				tableNumber: data.tableNumber,
				isDineIn: data.isDineIn,
				pickupName: data.takeOutName,
				restaurantDiscountAmount: data.restaurantDiscountAmount,
				selectedMethods: data.selectedMethods,
				posExtraData: data.posExtraData,
				redeemedRewards: data.redeemedRewards,
				pointsEarned: data.pointsEarned,
				timestampData: {
					...data.timestampData,
					PlacingOrder: new Date()?.toISOString?.(),
				},
				pickupPointObject: data.pickupPointObject,
				isSandbox: data.loadedData[global.org].environment === 'sandbox' || data.isTestMode,
				cartPrep: data.cartPrep,
				FCMToken,
				isDelivery: data.isDelivery,
				restaurantIds: data.restaurantIds,
				deliveryOrgReceivedPercent: data.deliveryOrgReceivedPercent,
				localTimezone: data.localTimezone,
				product: data.products[0],
				uId: data.userId,
			})
		} catch (error) {
			console.log(`[Checkout.Helpers.placeProductOrder] Encountered error. ${error.message}.`)
			console.log(error)
			data.toggleConfirmModal()
			data.dispatch(setCheckoutState({ spinner: false }))
			data.dispatch(
				showAlert({
					title: 'Error placing order',
					body: 'Please try again.',
				})
			)
			return
		}

		if (processedOrder?.created) {
			console.log(`[Checkout.Helpers.placeProductOrder] Processed order.`, processedOrder)
			const { created: orderCreated, orderId, orderNumber } = processedOrder

			if (orderCreated && orderId) {
				console.log(
					`[Checkout.Helpers.placeProductOrder] Created order. ID: ${orderId}, Number: ${orderNumber}.`
				)
				data.dispatch(
					setCheckoutState({
						orderId,
						orderNumber: orderNumber as any,
						orderCreated,
					})
				)
			}
		} else {
			console.log(`[Checkout.Helpers.placeProductOrder] Payment error.`, processedOrder)
		}

		return processedOrder
	}
}
